import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { useWizardState } from './useWizardState';
import { step2Schema, type Step2Input } from './schemas';
import { WizardShell } from './WizardShell';
import { cn } from '../../lib/cn';
import styles from './wizard.module.css';

const STEPS = ['ברוך הבא', 'פרטי העסק', 'לוקליזציה', 'סיכום'];

export function Step2Business() {
  const data = useWizardState((s) => s.data);
  const patch = useWizardState((s) => s.patch);
  const next = useWizardState((s) => s.next);
  const prev = useWizardState((s) => s.prev);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Step2Input>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      org_name: data.org_name,
      business_id: data.business_id,
      vat_id: data.vat_id,
    },
  });

  const onSubmit = handleSubmit((values) => {
    patch({
      org_name: values.org_name,
      business_id: values.business_id ?? '',
      vat_id: values.vat_id ?? '',
    });
    next();
  });

  return (
    <WizardShell
      step={1}
      steps={STEPS}
      foot={
        <>
          <button type="button" className={styles.btnSecondary} onClick={prev}>
            <ArrowRight size={14} className="flip-rtl" />
            חזור
          </button>
          <button type="submit" form="step2-form" className={styles.btnPrimary}>
            המשך
            <ArrowLeft size={14} className="flip-rtl" />
          </button>
        </>
      }
    >
      <h3>פרטי העסק</h3>
      <p className={styles.help}>השם יופיע בחשבוניות ובמסמכים. ע.מ. ועוסק מורשה אופציונליים — אפשר להוסיף בהמשך.</p>

      <form id="step2-form" onSubmit={onSubmit} noValidate>
        <div className={styles.field}>
          <label htmlFor="step2-org">שם העסק</label>
          <input
            id="step2-org"
            type="text"
            placeholder="שם החברה או העסק שלך"
            className={cn(styles.input, errors.org_name && styles.invalid)}
            {...register('org_name')}
          />
          {errors.org_name ? <p className={styles.error}>{errors.org_name.message}</p> : null}
        </div>

        <div className={styles.field} style={{ marginBlockStart: 'var(--space-3)' }}>
          <label htmlFor="step2-bizid">
            ע.מ. / ח.פ.
            <span className={styles.optional}>אופציונלי · 9 ספרות</span>
          </label>
          <input
            id="step2-bizid"
            type="text"
            inputMode="numeric"
            dir="ltr"
            placeholder="123456789"
            className={cn(styles.input, errors.business_id && styles.invalid)}
            {...register('business_id')}
          />
          {errors.business_id ? <p className={styles.error}>{errors.business_id.message}</p> : null}
        </div>

        <div className={styles.field} style={{ marginBlockStart: 'var(--space-3)' }}>
          <label htmlFor="step2-vatid">
            מספר עוסק מורשה
            <span className={styles.optional}>אופציונלי · 9 ספרות</span>
          </label>
          <input
            id="step2-vatid"
            type="text"
            inputMode="numeric"
            dir="ltr"
            placeholder="123456789"
            className={cn(styles.input, errors.vat_id && styles.invalid)}
            {...register('vat_id')}
          />
          {errors.vat_id ? <p className={styles.error}>{errors.vat_id.message}</p> : null}
        </div>
      </form>
    </WizardShell>
  );
}
